import { CommonModule, DatePipe } from '@angular/common';
import { Inject, Injectable, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRouteSnapshot, RouterModule, Routes } from '@angular/router';
import { DgxComponentsAccordionModule } from '@domgen/dgx-fe-components/accordion';
import { DgxComponentsButtonModule } from '@domgen/dgx-fe-components/button';
import { DgxComponentsFaqsModule } from '@domgen/dgx-fe-components/faq';
import { DgxComponentsIconModule } from '@domgen/dgx-fe-components/icon';
import { DgxComponentsLoadingModule } from '@domgen/dgx-fe-components/loading';
import { DgxComponentsModalModule } from '@domgen/dgx-fe-components/modal';
import { DgxComponentsNavigationModule } from '@domgen/dgx-fe-components/navigation';
import { CONFIG, FeatureEnabledDirective } from '@domgen/dgx-fe-config';
import { DgxGAModule, GAClickDirective, GAService } from '@domgen/dgx-fe-ga';
import { QMSanitiseDirective } from '@domgen/dgx-fe-qm';

import { TranslateModule } from '@ngx-translate/core';
import { ManageMyRepairComponent } from './containers/manage-my-repair/manage-my-repair.component';
import { ManageMyRepairContainerComponent } from './containers/manage-my-repair/manage-my-repair.container';
import { ManageMyRepairActiveClaimDetailsComponent } from './manage-my-repair/components/active-claim-details/active-claim-details.component';
import { ManageMyRepairClaimDetailContainerComponent } from './manage-my-repair/components/claim-detail-container/claim-detail-container.component';
import { ManageMyRepairAppointmentsHistoryComponent } from './manage-my-repair/components/appointments-history/appointments-history.component';
import { ManageMyRepairDetailGroupComponent } from './manage-my-repair/components/detail-group/detail-group.component';
import { ManageMyRepairDetailGroupSectionComponent } from './manage-my-repair/components/detail-group-section/detail-group-section.component';
import { ManageMyRepairNonActiveRepairStateComponent } from './manage-my-repair/components/non-active-repair-state/non-active-repair-state.component';
import { ManageMyRepairLoadingComponent } from './manage-my-repair/components/manage-my-repair-loading/manage-my-repair-loading.component';
import { AppointmentsHistoryLoaderComponent } from './manage-my-repair/components/appointments-history-loader/appointments-history-loader.component';
import {
  BookedTimeDatePipe,
  BookedTimePipe,
  RepairTrackerComponent,
} from './manage-my-repair/components/repair-tracker/repair-tracker.component';
import { FeedbackComponentComponent } from './manage-my-repair/components/feedback/feedback-component/feedback-component.component';
import { PhoneNumberComponent } from './manage-my-repair/components/repair-tracker/phone-number/phone-number.component';
import { PhoneDirectiveModule } from './directives/phone.directive.module';
import { PlanDetailsPipeModule } from './pipes/plan-detail-pipes.module';
import { ContactHoursComponentsModule } from './manage-my-repair/components/contact-hours/contact-hours.module';
import { NoSpacesPipeModule } from './pipes/noSpaces.pipe.module';
import { ManageMyRepairService } from './services/manage-my-repair.service';
import { AppliancesService } from './services/appliances.service';
import { ClaimsRedirectService } from './services/claims-redirect.service';
import { PhoneDirective } from './directives/phone.directive';
import { BrandCasePipe } from './pipes/brandcase.pipe';
import { CapitalCasePipe } from './pipes/capitalcase.pipe';
import { TimeFormatPipe } from './pipes/timeFormat.pipe';
import { UnavailableDocumentsComponent } from './manage-my-repair/components/non-active-repair-state/plan-docs/unavailable-docs.component';
import { MatDialogModule } from '@angular/material/dialog';
import { EngineerTrackingService } from './services/engineer-tracking.service';
import { ErrorRetryService } from './services/error-retry.service';
import { CmsService } from './services/cms.service';
import { SmartFixBannerModule } from './manage-my-repair/components/smart-fix-banner/smart-fix-banner.module';
import {
  AppConfig,
  FeatureFlagService,
  SharedComponentsUiModule,
} from '@domgen/dgx-components';
import { Observable, of } from 'rxjs';
import { NeedARepairComponent } from './containers/manage-my-repair/need-a-repair/need-a-repair.component';
import { MaintenanceBannerComponent } from './containers/manage-my-repair/maintenance-banner.component';
import { ManageMyRepairElectricalSafetyComponent } from './manage-my-repair/components/electrical-safety/electrical-safety.component';
import { ProductTypeQuestionComponent } from './containers/manage-my-repair/need-a-repair/product-type-question/product-type-question.component';
import { FeedbackCaptureComponent } from './manage-my-repair/components/feedback/feedback-capture/feedback-capture.component';

@Injectable({
  providedIn: 'root',
})
export class PostHogEnabledGuard {
  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,

    private readonly featureFlagService: FeatureFlagService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const featureFlagName = route.data.featureFlag;

    if (!featureFlagName) return of(true);

    const isFeatureEnabled =
      this.featureFlagService.isFeatureFlagEnabled(featureFlagName);

    return of(isFeatureEnabled);
  }
}

const routes: Routes = [
  {
    path: '',
    children: [
      {
        path: ':claimId/engineer-tracking',
        loadChildren: () =>
          import(
            './containers/engineer-tracking/engineer-tracking.module'
          ).then((m) => m.EngineerTrackingModule),
        data: {
          redirectTo: '/my-plans',
          featureFlag: 'engineerTracking',
        },
        canActivate: [PostHogEnabledGuard],
      },
      {
        path: '',
        pathMatch: 'full',
        component: ManageMyRepairContainerComponent,
      },
    ],
  },
];

@NgModule({
  declarations: [
    ManageMyRepairContainerComponent,
    ManageMyRepairComponent,
    ManageMyRepairActiveClaimDetailsComponent,
    ManageMyRepairClaimDetailContainerComponent,
    ManageMyRepairAppointmentsHistoryComponent,
    ManageMyRepairDetailGroupComponent,
    ManageMyRepairDetailGroupSectionComponent,
    ManageMyRepairNonActiveRepairStateComponent,
    ManageMyRepairLoadingComponent,
    MaintenanceBannerComponent,
    AppointmentsHistoryLoaderComponent,
    RepairTrackerComponent,
    BookedTimeDatePipe,
    BookedTimePipe,
    FeedbackComponentComponent,
    FeedbackCaptureComponent,
    PhoneNumberComponent,
    UnavailableDocumentsComponent,
    NeedARepairComponent,
    ManageMyRepairElectricalSafetyComponent,
    ProductTypeQuestionComponent,
  ],
  imports: [
    CommonModule,
    TranslateModule.forChild(),
    RouterModule.forChild(routes),
    FormsModule,
    DgxComponentsFaqsModule,
    DgxComponentsNavigationModule,
    DgxComponentsIconModule,
    DgxComponentsButtonModule,
    DgxComponentsLoadingModule,
    DgxComponentsModalModule,
    DgxGAModule,
    DgxComponentsAccordionModule,
    PhoneDirectiveModule,
    QMSanitiseDirective,
    PlanDetailsPipeModule,
    ContactHoursComponentsModule,
    GAClickDirective,
    FeatureEnabledDirective,
    NoSpacesPipeModule,
    MatDialogModule,
    SmartFixBannerModule,
    SharedComponentsUiModule,
    ReactiveFormsModule,
  ],
  providers: [
    ManageMyRepairService,
    EngineerTrackingService,
    ErrorRetryService,
    AppliancesService,
    ClaimsRedirectService,
    PhoneDirective,
    BrandCasePipe,
    CapitalCasePipe,
    TimeFormatPipe,
    GAService,
    DatePipe,
    CmsService,
  ],
  exports: [],
})
export class FeatureManageMyRepairModule {}
